/**
 * Navigation of the station services panel (M3 plan R16), pure: tabs across, the tab's buttons (plus Close, always
 * last) down. The DOM host feeds it keys (`servicesKey`) and pad inputs from M1's `stepPadNav`
 * (`servicesPadInput`); the mouse focuses and clicks directly.
 *
 * - Keys: ←/→ and Tab/Shift+Tab change tab, ↑/↓ move between buttons, Enter/Space press, Esc/Backspace close.
 * - Pad: D-pad or left stick left/right change tab, up/down move, A presses, B closes.
 */
import type { MenuInput } from '../../../hud/menu'

export type ServicesInput = 'prevTab' | 'nextTab' | 'prevItem' | 'nextItem' | 'activate' | 'close'

/** What an input did: nothing, changed the tab, moved the focus, pressed the focused button, or closed. */
export type ServicesNavResult = 'none' | 'tab' | 'focus' | 'activate' | 'close'

export interface ServicesNav {
  tab: number
  tabCount: number
  /** The focused button of the current tab; the last one is Close. */
  item: number
  itemCount: number
}

export function createServicesNav(tabCount: number): ServicesNav {
  return { tab: 0, tabCount: Math.max(0, tabCount), item: 0, itemCount: 1 }
}

/** The tab's buttons changed (a mount or a re-render): keep the focus in range. At least Close is there. */
export function setNavItems(nav: ServicesNav, count: number): void {
  nav.itemCount = Math.max(1, count)
  if (nav.item >= nav.itemCount) nav.item = nav.itemCount - 1
  if (nav.item < 0) nav.item = 0
}

/** Selects tab `index`; false when out of range or already selected. The focus returns to the first button. */
export function selectNavTab(nav: ServicesNav, index: number): boolean {
  if (index < 0 || index >= nav.tabCount || index === nav.tab) return false
  nav.tab = index
  nav.item = 0
  return true
}

/** Focuses button `index` (pointer hover); false when out of range or already focused. */
export function focusNavItem(nav: ServicesNav, index: number): boolean {
  if (index < 0 || index >= nav.itemCount || index === nav.item) return false
  nav.item = index
  return true
}

export function servicesNavInput(nav: ServicesNav, input: ServicesInput): ServicesNavResult {
  switch (input) {
    case 'prevTab':
    case 'nextTab': {
      if (nav.tabCount < 2) return 'none'
      const step = input === 'nextTab' ? 1 : nav.tabCount - 1
      selectNavTab(nav, (nav.tab + step) % nav.tabCount)
      return 'tab'
    }
    case 'prevItem':
    case 'nextItem': {
      if (nav.itemCount < 2) return 'none'
      const step = input === 'nextItem' ? 1 : nav.itemCount - 1
      nav.item = (nav.item + step) % nav.itemCount
      return 'focus'
    }
    case 'activate':
      return 'activate'
    case 'close':
      return 'close'
  }
}

/** The panel input for a key press (`KeyboardEvent.code`), or null for keys the panel leaves alone. */
export function servicesKey(code: string, shift: boolean): ServicesInput | null {
  switch (code) {
    case 'ArrowLeft':
      return 'prevTab'
    case 'ArrowRight':
      return 'nextTab'
    case 'Tab':
      return shift ? 'prevTab' : 'nextTab'
    case 'ArrowUp':
      return 'prevItem'
    case 'ArrowDown':
      return 'nextItem'
    case 'Enter':
    case 'NumpadEnter':
    case 'Space':
      return 'activate'
    case 'Escape':
    case 'Backspace':
      return 'close'
    default:
      return null
  }
}

/** The panel input for a pad menu input; Start is left to the loop (it reaches the panel through the Esc stack). */
export function servicesPadInput(input: MenuInput | null): ServicesInput | null {
  switch (input) {
    case 'left':
      return 'prevTab'
    case 'right':
      return 'nextTab'
    case 'up':
      return 'prevItem'
    case 'down':
      return 'nextItem'
    case 'activate':
      return 'activate'
    case 'back':
      return 'close'
    default:
      return null
  }
}
